import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");
  const [verifiedEmail, setVerifiedEmail] = useState("");

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setStatus("error");
      setMessage("Missing verification token. Please use the link from your email.");
      return;
    }
    axios.post(`${API}/auth/verify-email`, { token })
      .then((res) => {
        setStatus("success");
        setVerifiedEmail(res.data?.email || "");
        setMessage(res.data?.already_verified
          ? "This email was already verified."
          : "Your email is verified — you can log in now.");
      })
      .catch((err) => {
        setStatus("error");
        setMessage(err.response?.data?.detail || "This verification link is invalid or has expired.");
      });
  }, [params]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Card className="w-full max-w-md bg-white/95 backdrop-blur-sm border-slate-200 shadow-xl">
        <CardContent className="p-8 text-center space-y-5" data-testid="verify-email-card">
          {status === "loading" && (
            <>
              <Loader2 className="w-12 h-12 animate-spin text-cyan-600 mx-auto" />
              <p className="text-slate-600">Verifying your email…</p>
            </>
          )}
          {status === "success" && (
            <>
              <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center">
                <CheckCircle2 className="w-9 h-9 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800">You're verified!</h2>
              {verifiedEmail && (
                <p className="text-sm text-slate-500">{verifiedEmail}</p>
              )}
              <p className="text-slate-600">{message}</p>
              <Link to="/login">
                <Button
                  className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:opacity-90 text-white"
                  data-testid="verify-success-login-btn"
                >
                  Go to login
                </Button>
              </Link>
            </>
          )}
          {status === "error" && (
            <>
              <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
                <AlertCircle className="w-9 h-9 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800">Verification failed</h2>
              <p className="text-slate-600">{message}</p>
              <div className="flex flex-col gap-2">
                <Link to="/login">
                  <Button variant="outline" className="w-full" data-testid="verify-error-login-btn">Go to login</Button>
                </Link>
                <Link to="/signup">
                  <Button variant="ghost" className="w-full text-slate-500" data-testid="verify-error-signup-btn">Sign up again</Button>
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
